import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Radio, SkipBack, SkipForward } from 'lucide-react'
import { Select } from '../../../admin/primitives.js'
import { SegmentedControl } from '../../../admin/pickers.js'
import { Link } from '../../../router.js'
import { fmtDateHeure } from '../../api.js'
import {
  BandeauLecture, LIBELLE_ETAT, LIBELLE_GRAVITE, Pastille, PastilleEtat, TON_ETAT, TON_GRAVITE, nomSalle, useVueSonde, type Ton,
} from '../commun.js'
import {
  ETATS_AFFICHES, LIBELLE_RESOLUTION, appareilsATraiter, borner, cheminRejeu, duree, estRejeu, heureParis, libelleSource,
  position, reperesHoraires, sallesAuRejeu, trouDansFenetre, trousA,
  type Annotation, type AppareilRejeu, type Fenetre, type IncidentNuit, type ModeFenetre, type Nuit, type Rejeu as DocRejeu, type SalleAuRejeu,
} from './nuits-model.js'

/**
 * LE REJEU D'UNE NUIT — « magnétoscope » (21/10/2026).
 *
 * Un curseur de 22 h à 7 h (ou la journée entière, de 8 h à 8 h) ; à chaque
 * instant choisi, Sonde rend l'état de tout le club (`GET /v1/nights/:date/
 * replay?at&zone`). Tout est synchronisé sur le même instant : la frise (pannes,
 * retours, alertes par pas de 5 min, trous de supervision, incidents, notes),
 * les salles, les appareils, les incidents et les événements autour de T.
 *
 * Honnêteté : on peint `display_state` (STALE et UNAVAILABLE en gris, jamais
 * « hors ligne » pour une source muette) et on compte avec `display_counts`.
 * `collector_running === false` et `data_note_fr` sont affichés tels quels ;
 * un trou de supervision est hachuré gris sur la frise, jamais un zéro.
 */

const LIB_ALIM: Record<string, { ton: Ton; texte: string }> = {
  on: { ton: 'ok', texte: 'Allumée' },
  off: { ton: 'off', texte: 'Éteinte' },
  unknown: { ton: 'gris', texte: 'Alimentation inconnue' },
}

type Filtre = 'a_traiter' | 'tous'
const PAGE = 60

function depuis(since: string | null, debutNuit: number): string {
  if (!since) return '—'
  const t = Date.parse(since)
  return t < debutNuit ? fmtDateHeure(since) : heureParis(t)
}

/* ═══════════════ La frise ═══════════════ */

interface PropsFrise {
  f: Fenetre
  doc: DocRejeu | null
  nuit: Nuit
  incidents: IncidentNuit[]
  annotations: Annotation[]
  curseur: number
  onChoisir: (ms: number) => void
  libellesSources: Map<string, string>
}

function Voie({ nom, titre, fine, children }: { nom: string; titre?: string; fine?: boolean; children: ReactNode }) {
  return (
    <div className="sv-nuit-voie">
      <span className="sv-nuit-voie__nom" title={titre}>{nom}</span>
      <div className={`sv-nuit-voie__piste${fine ? ' sv-nuit-voie__piste--fine' : ''}`}>{children}</div>
    </div>
  )
}

function Barres({ points, cle, f, step, ton }: { points: DocRejeu['timeline']['points']; cle: 'down' | 'up' | 'events_warning'; f: Fenetre; step: number; ton: Ton }) {
  const max = Math.max(1, ...points.map((p) => p[cle]))
  const span = f.fin - f.debut
  const largeur = Math.max(1.5, (step * 1000) / Math.max(1, span) * 1000 - 1.2)
  return (
    <svg viewBox="0 0 1000 30" preserveAspectRatio="none" aria-hidden="true" className={`sv-ton--${ton}`}>
      {points.map((p) => {
        const v = p[cle]
        if (v <= 0) return null
        const h = Math.max(3, (v / max) * 28)
        return <rect key={p.at} className="sv-nuit-barre" x={position(Date.parse(p.at), f) * 10} y={30 - h} width={largeur} height={h} rx={1.5} />
      })}
    </svg>
  )
}

function Frise({ f, doc, nuit, incidents, annotations, curseur, onChoisir, libellesSources }: PropsFrise) {
  const axe = useRef<HTMLDivElement>(null)
  const [survol, setSurvol] = useState<number | null>(null)
  const step = doc?.timeline.step_s ?? 300
  const points = useMemo(() => (doc?.timeline.points ?? []).filter((p) => {
    const t = Date.parse(p.at)
    return t + step * 1000 > f.debut && t <= f.fin
  }), [doc, f.debut, f.fin, step])
  const reperes = reperesHoraires(f)
  const pas = reperes.length > 16 ? 3 : 1
  const trous = nuit.sources_unavailable.map((t) => ({ t, i: trouDansFenetre(t, f) })).filter((x) => x.i !== null)
  const ouv = nuit.opening_at ? Date.parse(nuit.opening_at) : null
  const ferm = nuit.closing_at ? Date.parse(nuit.closing_at) : null

  const instantDe = (clientX: number): number | null => {
    const r = axe.current?.getBoundingClientRect()
    if (!r || r.width <= 0 || clientX < r.left - 6) return null
    const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return borner(f.debut + ratio * (f.fin - f.debut), f)
  }
  const bas = (e: ReactPointerEvent<HTMLDivElement>) => {
    const t = instantDe(e.clientX)
    if (t === null) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    onChoisir(t)
  }
  const bouge = (e: ReactPointerEvent<HTMLDivElement>) => {
    const t = instantDe(e.clientX)
    setSurvol(t)
    if (t !== null && (e.buttons & 1) === 1) onChoisir(t)
  }

  // La lecture : ce que montre la frise à l'instant survolé (ou au curseur).
  const lu = survol ?? curseur
  const pt = points.find((p) => { const t = Date.parse(p.at); return lu >= t && lu < t + step * 1000 }) ?? null
  const trousLus = trousA(nuit.sources_unavailable, lu)
  const lecture = [
    `${heureParis(pt ? Date.parse(pt.at) : lu)}${pt ? ` – ${heureParis(Date.parse(pt.at) + step * 1000)}` : ''}`,
    pt ? `${pt.down} passage${pt.down > 1 ? 's' : ''} en panne ou dégradé` : null,
    pt ? `${pt.up} retour${pt.up > 1 ? 's' : ''} en ligne` : null,
    pt ? `${pt.events_warning} alerte${pt.events_warning > 1 ? 's' : ''}` : null,
    trousLus.length ? `trou de supervision : ${trousLus.map((t) => libelleSource(t.source, libellesSources)).join(', ')}` : null,
  ].filter(Boolean).join(' · ')

  return (
    <>
      <div className="sv-nuit-frise" onPointerDown={bas} onPointerMove={bouge} onPointerLeave={() => setSurvol(null)}
           role="group" aria-label="Frise de la nuit : cliquer pour choisir l’instant">
        <Voie nom="Pannes" titre="Passages en panne ou dégradé, par pas de 5 min">
          <Barres points={points} cle="down" f={f} step={step} ton="err" />
        </Voie>
        <Voie nom="Retours" titre="Retours en ligne, par pas de 5 min">
          <Barres points={points} cle="up" f={f} step={step} ton="ok" />
        </Voie>
        <Voie nom="Alertes" titre="Événements d’avertissement ou plus graves, par pas de 5 min">
          <Barres points={points} cle="events_warning" f={f} step={step} ton="warn" />
        </Voie>
        <Voie nom="Supervision" titre="Trous de supervision : sources muettes, appareils non vus" fine>
          {trous.map(({ t, i }) => (
            <span key={`${t.source}-${t.from}`} className="sv-nuit-trou"
                  style={{ left: `${position(i!.de, f)}%`, width: `${Math.max(0.4, position(i!.a, f) - position(i!.de, f))}%` }}
                  title={`${libelleSource(t.source, libellesSources)} : muette de ${heureParis(t.from)} à ${t.to ? heureParis(t.to) : 'maintenant'} (${duree(t.seconds)})`} />
          ))}
        </Voie>
        <Voie nom="Incidents" fine>
          {incidents.map((i) => {
            const de = Date.parse(i.opened_at)
            const a = i.resolved_at ? Date.parse(i.resolved_at) : f.fin
            if (a < f.debut || de > f.fin) return null
            return (
              <span key={i.id} className={`sv-nuit-inc sv-ton--${TON_GRAVITE[i.severity] ?? 'gris'}`}
                    style={{ left: `${position(de, f)}%`, width: `${Math.max(0.5, position(a, f) - position(de, f))}%` }}
                    title={`${i.title} — ${heureParis(i.opened_at)} → ${i.resolved_at ? heureParis(i.resolved_at) : 'non résolu'}`} />
            )
          })}
        </Voie>
        <Voie nom="Notes" fine>
          {annotations.map((a) => {
            const t = Date.parse(a.at)
            if (t < f.debut || t > f.fin) return null
            return <span key={a.id} className="sv-nuit-note" style={{ left: `${position(t, f)}%` }} title={`${heureParis(t)} · ${a.kind_label} : ${a.body}`} />
          })}
        </Voie>
        <div className="sv-nuit-axe" ref={axe} aria-hidden="true">
          {reperes.map((r, k) => k % pas === 0 ? <span key={r.at} style={{ left: `${position(r.at, f)}%` }}>{r.libelle}</span> : null)}
        </div>
        <div className="sv-nuit-pistes" aria-hidden="true">
          {reperes.map((r) => <span key={r.at} className="sv-nuit-repere" style={{ left: `${position(r.at, f)}%` }} />)}
          {ouv !== null && ouv >= f.debut && ouv <= f.fin ? <span className="sv-nuit-repere sv-nuit-repere--plein" style={{ left: `${position(ouv, f)}%` }} /> : null}
          {ferm !== null && ferm >= f.debut && ferm <= f.fin ? <span className="sv-nuit-repere sv-nuit-repere--plein" style={{ left: `${position(ferm, f)}%` }} /> : null}
          {survol !== null ? <span className="sv-nuit-survol" style={{ left: `${position(survol, f)}%` }} /> : null}
          <span className="sv-nuit-curseur" style={{ left: `${position(curseur, f)}%` }} />
        </div>
      </div>
      <p className="sv-nuit-lecture" aria-live="off">{lecture}</p>
    </>
  )
}

/* ═══════════════ Les salles à l'instant T ═══════════════ */

function CarteSalle({ s, libelle, choisie, onChoisir }: { s: SalleAuRejeu; libelle: string; choisie: boolean; onChoisir: () => void }) {
  const alim = s.power ? LIB_ALIM[s.power] : null
  const segments = ETATS_AFFICHES.map((e) => ({ e, n: s.comptes[e] ?? 0 })).filter((x) => x.n > 0)
  const detail = segments.map((x) => `${x.n} ${(LIBELLE_ETAT[x.e] ?? x.e).toLowerCase()}`).join(', ')
  return (
    <button type="button" className={`sv-nuit-salle sv-ton--${s.ton}${choisie ? ' sv-nuit-salle--choisie' : ''}`} onClick={onChoisir}
            aria-pressed={choisie} title={choisie ? 'Revoir tout le club' : 'Rejouer cette salle seule'}>
      <span className="sv-nuit-salle__haut">
        <span className="sv-nuit-salle__nom">{libelle}</span>
        {s.cut ? <Pastille ton="err">Coupure de courant</Pastille> : <Pastille ton={alim?.ton ?? 'gris'}>{alim?.texte ?? 'Alimentation : —'}</Pastille>}
      </span>
      {s.total > 0 ? (
        <>
          <span className="sv-nuit-pile" aria-hidden="true">
            {segments.map((x) => <span key={x.e} className={`sv-ton--${TON_ETAT[x.e] ?? 'gris'}`} style={{ flexGrow: x.n }} />)}
          </span>
          <span className="sv-nuit-salle__meta">{s.total} appareil{s.total > 1 ? 's' : ''} : {detail}</span>
        </>
      ) : (
        <span className="sv-nuit-salle__meta">Aucun appareil de cette salle au registre de Sonde. Les équipements découverts attendent dans « À valider ».</span>
      )}
      <span className="sv-nuit-salle__meta">
        {s.since ? `${s.power === 'on' ? 'Allumée' : s.power === 'off' ? 'Éteinte' : 'État'} depuis ${heureParis(s.since)}` : 'Aucun changement d’alimentation connu'}
        {s.incidents > 0 ? ` · ${s.incidents} incident${s.incidents > 1 ? 's' : ''} ouvert${s.incidents > 1 ? 's' : ''}` : ''}
      </span>
    </button>
  )
}

/* ═══════════════ Appareils ═══════════════ */

function LigneAppareil({ d, debutNuit, libelleZone }: { d: AppareilRejeu; debutNuit: number; libelleZone: string }) {
  const gris = d.display_state === 'STALE' ? 'observation trop ancienne : état non vérifié à cet instant'
    : d.display_state === 'UNAVAILABLE' ? 'sa source ne répondait pas : appareil non vu, ni en ligne ni en panne' : null
  const dernier = d.status && d.status !== d.display_state && d.status !== 'UNKNOWN' ? `dernier état connu : ${(LIBELLE_ETAT[d.status] ?? d.status).toLowerCase()}` : null
  const meta = [
    libelleZone, d.family, d.kind,
    d.uplink ? `via ${d.uplink.switch_name}${d.uplink.port !== null ? ` port ${d.uplink.port}` : ''}` : null,
    d.cause ? `cause : ${d.cause}` : null, gris, dernier,
  ].filter(Boolean).join(' · ')
  return (
    <li className="sv-nuit-l">
      <span className="sv-nuit-l__t" title="Depuis">{depuis(d.since, debutNuit)}</span>
      <div className="sv-nuit-l__corps">
        <span className="sv-nuit-l__titre">
          <Link href={`/infrastructure/appareils/${encodeURIComponent(d.uid)}`}>{d.name}</Link>
          <PastilleEtat etat={d.display_state} />
          {d.flapping ? <Pastille ton="warn">Instable : {d.transitions_10min} bascules en 10 min</Pastille> : null}
        </span>
        <span className="sv-nuit-l__meta">{meta}</span>
      </div>
    </li>
  )
}

/* ═══════════════ Le rejeu ═══════════════ */

export interface PropsRejeu {
  nuit: Nuit
  fenetre: Fenetre | null
  mode: ModeFenetre
  onMode: (m: ModeFenetre) => void
  curseur: number | null
  onCurseur: (ms: number) => void
  incidents: IncidentNuit[]
  annotations: Annotation[]
  libellesZones: Map<string, string>
  libellesSources: Map<string, string>
  /** sonde.details.read : contexte technique (clés d'entité, mesures brutes). */
  detailTechnique: boolean
}

export function RejeuNuit(p: PropsRejeu) {
  const { nuit, fenetre: f, curseur } = p
  const [zone, setZone] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<Filtre>('a_traiter')
  const [limite, setLimite] = useState(PAGE)
  const [atCharge, setAtCharge] = useState<number | null>(curseur)

  // Le curseur bouge à chaque pixel : Sonde n'est relue qu'une fois le geste posé.
  useEffect(() => {
    const t = setTimeout(() => setAtCharge(curseur), 250)
    return () => clearTimeout(t)
  }, [curseur])

  const chemin = atCharge !== null ? cheminRejeu(nuit.night_date, atCharge, zone) : null
  const { lecture, precedente } = useVueSonde(chemin, estRejeu, 300_000)
  const r = lecture?.etat === 'ok' ? lecture.data : precedente
  const aJour = r !== null && curseur !== null && Date.parse(r.at) === curseur && (r.zone ?? null) === zone

  const debutNuit = Date.parse(nuit.starts_at)
  const notes = zone ? p.annotations.filter((a) => !a.zone_code || a.zone_code === zone) : p.annotations

  // Les « moments » où quelque chose change : pour sauter d'un fait au suivant.
  const moments = useMemo(() => {
    const m = new Set<number>()
    const step = (r?.timeline.step_s ?? 300) * 1000
    for (const pt of r?.timeline.points ?? []) if (pt.down + pt.up + pt.events_warning > 0) m.add(Date.parse(pt.at) + Math.round(step / 2))
    for (const i of p.incidents) { m.add(Date.parse(i.opened_at)); if (i.resolved_at) m.add(Date.parse(i.resolved_at)) }
    for (const a of notes) m.add(Date.parse(a.at))
    for (const t of nuit.sources_unavailable) { m.add(Date.parse(t.from)); if (t.to) m.add(Date.parse(t.to)) }
    return [...m].filter(Number.isFinite).sort((a, b) => a - b)
  }, [r?.timeline, p.incidents, notes, nuit.sources_unavailable])

  if (!f) {
    return (
      <section className="lc-panel sv-nuit-section" id="sv-nuit-rejeu" aria-labelledby="sv-nuit-rejeu-t">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-nuit-rejeu-t">Rejeu</h2></header>
        <div className="lc-panel__body"><p className="sv-nuit-vide">Rien à rejouer : cette nuit n’a pas encore commencé.</p></div>
      </section>
    )
  }
  const c = curseur ?? f.debut
  const aller = (ms: number) => p.onCurseur(borner(ms, f))
  const prec = [...moments].reverse().find((m) => m < c - 30_000 && m >= f.debut)
  const suiv = moments.find((m) => m > c + 30_000 && m <= f.fin)
  const salles = r ? sallesAuRejeu(r) : []
  const principales = salles.filter((s) => ['R1', 'R2', 'R3'].includes(s.code) || s.code === zone)
  const autres = salles.filter((s) => !principales.includes(s))
  const appareils = r ? (filtre === 'a_traiter' ? appareilsATraiter(r.devices) : [...r.devices]) : []
  const evenements = r ? [...r.events].sort((a, b) => b.at.localeCompare(a.at)) : []
  const incidentsT = r ? [...r.incidents].sort((a, b) => Number(b.open_at_t) - Number(a.open_at_t) || b.opened_at.localeCompare(a.opened_at)) : []
  const trousT = trousA(nuit.sources_unavailable, c)
  const total = r ? Object.values(r.display_counts).reduce((s, n) => s + n, 0) : 0
  const zonesOptions = [{ value: '', label: 'Tout le club' }, ...[...p.libellesZones.entries()].map(([code, label]) => ({ value: code, label }))]
  if (zone && !p.libellesZones.has(zone)) zonesOptions.push({ value: zone, label: nomSalle(zone) })
  const lib = (code: string) => code === '—' ? 'Sans salle' : p.libellesZones.get(code) ?? nomSalle(code)
  const choisirZone = (code: string) => { setZone((z) => (z === code || code === '—' ? null : code)); setLimite(PAGE) }
  const soireeDispo = p.mode === 'soiree' && f.mode === 'journee'

  return (
    <section className="lc-panel sv-nuit-section" id="sv-nuit-rejeu" aria-labelledby="sv-nuit-rejeu-t">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="sv-nuit-rejeu-t">Rejeu</h2>
        <span className="lc-panel__spacer" />
        <SegmentedControl size="sm" value={f.mode} onChange={p.onMode} ariaLabel="Fenêtre du rejeu"
                          options={[{ value: 'soiree', label: '22 h → 7 h' }, { value: 'journee', label: '8 h → 8 h' }]} />
      </header>
      <div className="lc-panel__body">
        {soireeDispo ? <p className="sv-nuit-vide">La soirée (22 h → 7 h) n’a pas encore commencé : le rejeu couvre la journée jusqu’à maintenant.</p> : null}
        <div className="sv-nuit-commandes">
          <div className="sv-nuit-instant" aria-live="polite">
            <span className="sv-nuit-instant__heure">{heureParis(c)}</span>
            <span className="sv-nuit-instant__sous">
              heure de Paris{r ? ` · ${LIBELLE_RESOLUTION[r.resolution] ?? r.resolution}` : ''}{aJour || !curseur ? '' : ' · lecture…'}
            </span>
          </div>
          <span className="lc-panel__spacer" />
          <div className="sv-nuit-pas">
            <button type="button" className="lc-btn" onClick={() => prec !== undefined && aller(prec)} disabled={prec === undefined} title="Fait précédent" aria-label="Fait précédent"><SkipBack size={14} aria-hidden="true" /></button>
            <button type="button" className="lc-btn" onClick={() => aller(c - 300_000)} title="5 minutes avant"><ChevronsLeft size={14} aria-hidden="true" /> 5 min</button>
            <button type="button" className="lc-btn" onClick={() => aller(c - 60_000)} title="1 minute avant" aria-label="1 minute avant"><ChevronLeft size={14} aria-hidden="true" /></button>
            <button type="button" className="lc-btn" onClick={() => aller(c + 60_000)} title="1 minute après" aria-label="1 minute après"><ChevronRight size={14} aria-hidden="true" /></button>
            <button type="button" className="lc-btn" onClick={() => aller(c + 300_000)} title="5 minutes après">5 min <ChevronsRight size={14} aria-hidden="true" /></button>
            <button type="button" className="lc-btn" onClick={() => suiv !== undefined && aller(suiv)} disabled={suiv === undefined} title="Fait suivant" aria-label="Fait suivant"><SkipForward size={14} aria-hidden="true" /></button>
            {f.enCours ? <button type="button" className="lc-btn" onClick={() => aller(f.fin)}><Radio size={14} aria-hidden="true" /> Maintenant</button> : null}
          </div>
        </div>

        <Frise f={f} doc={r} nuit={nuit} incidents={p.incidents} annotations={notes} curseur={c} onChoisir={aller} libellesSources={p.libellesSources} />
        <input className="sv-nuit-range" type="range" min={f.debut} max={f.fin} step={60_000} value={c}
               onChange={(e) => aller(Number(e.target.value))} aria-label="Instant rejoué" aria-valuetext={heureParis(c)} />

        <BandeauLecture lecture={lecture} depuis={r?.at ?? null} />
        {r?.collector_running === false ? (
          <p className="sv-bandeau sv-bandeau--gris" role="status">Collecteur de Sonde arrêté à cet instant : les états sont figés au dernier changement connu, grisés.</p>
        ) : null}
        {r?.data_note_fr ? <p className="sv-bandeau sv-bandeau--gris" role="status">{r.data_note_fr}</p> : null}
        {r && r.collector_running === null ? <p className="sv-bandeau sv-bandeau--gris" role="status">Impossible de dire si Sonde relevait à cet instant.</p> : null}
        {trousT.length > 0 ? (
          <p className="sv-bandeau sv-bandeau--gris" role="status">
            Trou de supervision à cet instant : {trousT.map((t) => `${libelleSource(t.source, p.libellesSources)} (de ${heureParis(t.from)} à ${t.to ? heureParis(t.to) : 'maintenant'})`).join(', ')}.
            Leurs appareils n’ont pas été vus : gris, ni en ligne ni en panne.
          </p>
        ) : null}

        {r ? (
          <>
            <div className="sv-nuit-commandes sv-nuit-section">
              <div className="sv-nuit-etats" aria-label="Appareils par état affiché">
                {ETATS_AFFICHES.filter((e) => (r.display_counts[e] ?? 0) > 0).map((e) => (
                  <Pastille key={e} ton={TON_ETAT[e] ?? 'gris'}>{LIBELLE_ETAT[e] ?? e} : {r.display_counts[e]}</Pastille>
                ))}
                {total === 0 ? <span className="sv-nuit-vide">Aucun appareil au registre de Sonde {zone ? 'dans cette zone' : ''} à cet instant.</span> : null}
              </div>
              <span className="lc-panel__spacer" />
              <div className="sv-nuit-zone">
                <Select value={zone ?? ''} options={zonesOptions} onChange={(v) => { setZone(v || null); setLimite(PAGE) }} ariaLabel="Zone rejouée" compact />
              </div>
            </div>

            <div className="sv-nuit-salles sv-nuit-section">
              {principales.map((s) => <CarteSalle key={s.code} s={s} libelle={lib(s.code)} choisie={zone === s.code} onChoisir={() => choisirZone(s.code)} />)}
            </div>
            {autres.length > 0 ? (
              <div className="sv-nuit-salles sv-nuit-salles--petites">
                {autres.map((s) => <CarteSalle key={s.code} s={s} libelle={lib(s.code)} choisie={zone === s.code} onChoisir={() => choisirZone(s.code)} />)}
              </div>
            ) : null}

            <div className="sv-nuit-deux">
              <section className="lc-panel" aria-labelledby="sv-nuit-inc-t">
                <header className="lc-panel__head">
                  <h3 className="lc-section-title" id="sv-nuit-inc-t">Incidents à {heureParis(c)}</h3>
                  <span className="lc-panel__spacer" />
                  <span className="lc-hint">{incidentsT.filter((i) => i.open_at_t).length} ouvert{incidentsT.filter((i) => i.open_at_t).length > 1 ? 's' : ''}</span>
                </header>
                <div className="lc-panel__body">
                  {incidentsT.length === 0 ? <p className="sv-nuit-vide">Aucun incident ouvert à cet instant, ni dans les 5 minutes autour.</p> : (
                    <ul className="sv-nuit-lignes">
                      {incidentsT.map((i) => (
                        <li key={i.id} className={`sv-nuit-l${i.open_at_t ? '' : ' sv-nuit-l--ombre'}`}>
                          <span className="sv-nuit-l__t">{heureParis(i.opened_at)}</span>
                          <div className="sv-nuit-l__corps">
                            <span className="sv-nuit-l__titre">
                              <Pastille ton={TON_GRAVITE[i.severity] ?? 'gris'}>{LIBELLE_GRAVITE[i.severity] ?? i.severity}</Pastille>
                              <Link href={`/infrastructure/incidents/${encodeURIComponent(i.id)}`}>{i.title}</Link>
                            </span>
                            <span className="sv-nuit-l__meta">
                              {i.open_at_t ? 'Ouvert à cet instant' : Date.parse(i.opened_at) > c ? 'S’ouvre dans les 5 minutes' : 'Résolu juste avant'}
                              {` · ${i.resolved_at ? `résolu à ${heureParis(i.resolved_at)}` : 'pas encore résolu'}`}
                              {i.affected_count > 0 ? ` · ${i.affected_count} appareil${i.affected_count > 1 ? 's' : ''} touché${i.affected_count > 1 ? 's' : ''}` : ''}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </section>

              <section className="lc-panel" aria-labelledby="sv-nuit-ev-t">
                <header className="lc-panel__head">
                  <h3 className="lc-section-title" id="sv-nuit-ev-t">Autour de {heureParis(c)}</h3>
                  <span className="lc-panel__spacer" />
                  <span className="lc-hint">5 min avant, 1 min après</span>
                </header>
                <div className="lc-panel__body">
                  {evenements.length === 0 ? <p className="sv-nuit-vide">Aucun événement enregistré dans ces 6 minutes.</p> : (
                    <ul className="sv-nuit-lignes">
                      {evenements.slice(0, 40).map((e, k) => (
                        <li key={`${e.at}-${e.kind}-${k}`} className="sv-nuit-l">
                          <span className="sv-nuit-l__t">{heureParis(e.at, true)}</span>
                          <div className="sv-nuit-l__corps">
                            <span className="sv-nuit-l__titre">
                              <Pastille ton={TON_GRAVITE[e.severity] ?? 'gris'}>{LIBELLE_GRAVITE[e.severity] ?? e.severity}</Pastille>
                              <span>{e.message_fr ?? e.kind}</span>
                            </span>
                            {e.uid ? <span className="sv-nuit-l__meta"><Link href={`/infrastructure/appareils/${encodeURIComponent(e.uid)}`}>Voir l’appareil</Link></span> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                  {evenements.length > 40 ? <p className="sv-nuit-vide sv-nuit-plus">{evenements.length - 40} autres événements dans ces 6 minutes.</p> : null}
                </div>
              </section>
            </div>

            <section className="lc-panel sv-nuit-section" aria-labelledby="sv-nuit-app-t">
              <header className="lc-panel__head">
                <h3 className="lc-section-title" id="sv-nuit-app-t">Appareils à {heureParis(c)}{zone ? ` · ${lib(zone)}` : ''}</h3>
                <span className="lc-panel__spacer" />
                <SegmentedControl size="sm" value={filtre} onChange={(v) => { setFiltre(v); setLimite(PAGE) }} ariaLabel="Appareils montrés"
                                  options={[{ value: 'a_traiter', label: `Pas en ligne (${appareilsATraiter(r.devices).length})` }, { value: 'tous', label: `Tous (${r.devices.length})` }]} />
              </header>
              <div className="lc-panel__body">
                {appareils.length === 0 ? (
                  <p className="sv-nuit-vide">
                    {r.devices.length === 0
                      ? <>Aucun appareil au registre de Sonde pour cette zone. Les équipements découverts (caméras, TPE, nœuds lumière…) attendent d’être validés dans <Link href="/infrastructure/registre">« À valider »</Link>.</>
                      : 'Tout ce que Sonde connaît était en ligne ou éteint comme prévu à cet instant.'}
                  </p>
                ) : (
                  <ul className="sv-nuit-lignes">
                    {appareils.slice(0, limite).map((d) => <LigneAppareil key={d.uid} d={d} debutNuit={debutNuit} libelleZone={d.zone ? lib(d.zone) : 'Sans salle'} />)}
                  </ul>
                )}
                {appareils.length > limite ? (
                  <button type="button" className="lc-btn sv-nuit-plus" onClick={() => setLimite((n) => n + PAGE)}>Afficher les {Math.min(PAGE, appareils.length - limite)} suivants ({appareils.length - limite} restants)</button>
                ) : null}
                {p.detailTechnique && r.context.length > 0 ? (
                  <details className="sv-nuit-details sv-nuit-plus">
                    <summary>Contexte mesuré à cet instant ({r.context.length})</summary>
                    <ul className="sv-nuit-lignes">
                      {r.context.map((x) => (
                        <li key={`${x.entity_key}-${x.metric}`} className="sv-nuit-l">
                          <span className="sv-nuit-l__t">{heureParis(x.at)}</span>
                          <div className="sv-nuit-l__corps"><span className="sv-nuit-l__meta"><span className="sv-nuit-mono">{x.entity_key}</span> · {x.metric} = {x.value ?? '—'}</span></div>
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : null}
              </div>
            </section>
          </>
        ) : !lecture ? <p className="sv-nuit-vide sv-nuit-section">Lecture du rejeu…</p> : null}
      </div>
    </section>
  )
}
